import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PageHeader from '../../components/common/PageHeader';
import EmptyData from '../../components/UI/EmptyData';
import { ListSkeleton } from '../../components/UI/Skeleton/Skeleton';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import {
  useGetHRXNotificationQuery,
  useUpdateNotificationReadStatusMutation
} from '../../redux/apis/Notification';

import { GLOBAL, NOTIFICATION_TYPES_KEYS } from '../../utils/constant/global';
import { getRelativeTime, handleApiError, skipRow, unique } from '../../utils/handler';

import './NotificationsPage.scss';

const { DESC } = GLOBAL;

// Same type → icon/tone mapping used by the sidebar bell, kept in sync.
const NOTIFICATION_TYPE_META = {
  [NOTIFICATION_TYPES_KEYS.NT_SUPPORT_TICKET]: { icon: 'icofont-ticket', tone: 'violet' },
  [NOTIFICATION_TYPES_KEYS.NT_LEAVE_REQUEST]: { icon: 'icofont-beach-bed', tone: 'teal' },
  [NOTIFICATION_TYPES_KEYS.NT_ANNOUNCEMENT]: { icon: 'icofont-megaphone', tone: 'amber' },
  [NOTIFICATION_TYPES_KEYS.NT_HOLIDAY]: { icon: 'icofont-gift', tone: 'rose' },
  [NOTIFICATION_TYPES_KEYS.NT_PERFORMANCE_REVIEW]: { icon: 'icofont-chart-growth', tone: 'indigo' }
};

const getTypeMeta = (type) =>
  NOTIFICATION_TYPE_META[type] || { icon: 'icofont-bell-alt', tone: 'slate' };

const ROUTE_BY_TYPE = {
  [NOTIFICATION_TYPES_KEYS.NT_SUPPORT_TICKET]: '/support-ticket',
  [NOTIFICATION_TYPES_KEYS.NT_LEAVE_REQUEST]: '/leave-request',
  [NOTIFICATION_TYPES_KEYS.NT_ANNOUNCEMENT]: '/announcement',
  [NOTIFICATION_TYPES_KEYS.NT_HOLIDAY]: '/holidays',
  [NOTIFICATION_TYPES_KEYS.NT_PERFORMANCE_REVIEW]: '/profile'
};

const PAGE_SIZE = 15;
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' }
];

function NotificationsPage() {
  const navigate = useNavigate();
  const { data: loginData } = useIsLoggedInQuery();
  const userId = loginData?.data?._id;

  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState([]);
  const [isNext, setIsNext] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const {
    data: notificationData,
    isFetching,
    refetch
  } = useGetHRXNotificationQuery(
    {
      search: '',
      skip: skipRow(currentPage, PAGE_SIZE),
      limit: PAGE_SIZE,
      sort: 'time',
      sortType: DESC,
      userId
    },
    { skip: !userId }
  );

  const [updateNotificationReadStatus] = useUpdateNotificationReadStatusMutation();

  useEffect(() => {
    const list = notificationData?.data?.notificationList;
    if (!list?.length) {
      if (currentPage === 1) setItems([]);
      return;
    }
    setItems((prev) => (currentPage === 1 ? list : unique([...prev, ...list], '_id')));
    setIsNext(Boolean(notificationData?.isNext));
  }, [notificationData, currentPage]);

  const totalCount = notificationData?.data?.totalCount ?? items.length;
  const unreadCount = notificationData?.data?.totalUnReadCount ?? 0;

  const visibleItems = useMemo(() => {
    if (activeFilter === 'unread') return items.filter((n) => !n?.readStatus);
    if (activeFilter === 'read') return items.filter((n) => Boolean(n?.readStatus));
    return items;
  }, [items, activeFilter]);

  const handleOpen = async (notification) => {
    const routeUrl = ROUTE_BY_TYPE[notification?.data?.type] || '';
    try {
      const { data: resData, error } = await updateNotificationReadStatus({ id: notification._id });
      if (resData?.success) {
        setItems([]);
        setCurrentPage(1);
        refetch();
        if (routeUrl) navigate(routeUrl);
      } else if (error) {
        handleApiError(error);
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadMore = () => {
    if (isNext) setCurrentPage((p) => p + 1);
  };

  const initialLoading = isFetching && currentPage === 1 && items.length === 0;

  return (
    <div className="container-xxl">
      <PageHeader headerTitle="Notifications" />

      <div className="row clearfix g-3">
        <div className="col-sm-12">
          <div className="notifications-page">
            <div className="notifications-page__toolbar">
              <div className="notifications-page__filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`notifications-page__filter ${activeFilter === f.key ? 'is-active' : ''}`}
                    onClick={() => setActiveFilter(f.key)}
                  >
                    {f.label}
                    {f.key === 'unread' && unreadCount > 0 ? (
                      <span className="notifications-page__filter-count">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
              <div className="notifications-page__summary">
                {totalCount} total
                {unreadCount > 0 ? ` · ${unreadCount} unread` : ''}
              </div>
            </div>

            <div className="notifications-page__body">
              {initialLoading ? (
                <ListSkeleton rows={8} />
              ) : visibleItems.length > 0 ? (
                <ul className="notifications-page__list">
                  {visibleItems.map((notification) => {
                    const isRead = Boolean(notification?.readStatus);
                    const meta = getTypeMeta(notification?.data?.type);
                    return (
                      <li key={notification._id}>
                        <button
                          type="button"
                          className={`notifications-page__row ${isRead ? 'is-read' : 'is-unread'}`}
                          onClick={() => handleOpen(notification)}
                        >
                          <span
                            className={`notifications-page__icon notifications-page__icon--${meta.tone}`}
                            aria-hidden="true"
                          >
                            <i className={`icofont ${meta.icon}`} />
                          </span>
                          <span className="notifications-page__main">
                            <span className="notifications-page__title">
                              {notification?.title || 'Notification'}
                            </span>
                            {notification?.body ? (
                              <span className="notifications-page__text">{notification.body}</span>
                            ) : null}
                          </span>
                          <span className="notifications-page__meta">
                            {!isRead ? (
                              <span className="notifications-page__dot" aria-hidden="true" />
                            ) : null}
                            <time
                              className="notifications-page__time"
                              dateTime={notification?.time}
                            >
                              {notification?.time ? getRelativeTime(notification.time) : ''}
                            </time>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="notifications-page__empty">
                  <EmptyData />
                </div>
              )}

              {visibleItems.length > 0 && isNext ? (
                <div className="notifications-page__footer">
                  <button
                    type="button"
                    className="notifications-page__load-more"
                    onClick={loadMore}
                    disabled={isFetching}
                  >
                    {isFetching ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationsPage;
